'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { readCurrencyCookie } from '@/lib/pricing/client';
import { getSubscriptionPlans } from '@/lib/admin/subscriptionPlansCache';

interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  currency: string;
  stripe_product_id: string;
  stripe_price_monthly_id: string;
  stripe_price_yearly_id: string;
  max_employees: number;
  max_locations: number;
  max_accommodations: number;
  features: Record<string, boolean>;
  is_active: boolean;
  sort_order: number;
}

interface TenantSubscription {
  id: string;
  tenant_id: string;
  billing_user_id: string;
  plan_id: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  status: 'trial' | 'active' | 'past_due' | 'canceled' | 'unpaid' | 'paused';
  billing_cycle: 'monthly' | 'yearly';
  current_period_start?: string;
  current_period_end?: string;
  trial_start: string;
  trial_end: string;
  trial_used: boolean;
  accommodation_count: number;
  created_at: string;
  updated_at: string;
  canceled_at?: string;
  /** TRUE when the subscription is scheduled to end at `current_period_end`
   *  (user clicked Cancel + "end of period"). UI uses this to render an
   *  "Endet am …" banner instead of "Nächste Abrechnung am …". Persisted
   *  from Stripe webhook `customer.subscription.updated` after schema
   *  fix on 2026-05-29 (Real-Card-Test). */
  cancel_at_period_end?: boolean;
  /** ISO timestamp the scheduled cancellation takes effect. Usually
   *  equals `current_period_end`; Stripe lets the user pick earlier. */
  cancel_at?: string | null;
  /** ISO-4217 currency the subscription is billed in (EUR/USD/GBP).
   *  Defaults to 'EUR' for legacy rows via the DB column default. */
  currency?: string;
  plan: SubscriptionPlan;
  trial_days_remaining: number;
  is_trial_expired: boolean;
}

interface TenantSubscriptionContextType {
  // Data
  plans: SubscriptionPlan[];
  currentSubscription: TenantSubscription | null;
  hasSubscription: boolean;
  isTrialExpired: boolean;
  trialDaysRemaining: number;
  /** Tenant hat Subscription-Bypass (`tenants.bypass_subscription_check`).
   *  Trial-/Past-due-Banner werden dann unterdrückt. */
  bypass: boolean;

  // Loading states
  plansLoading: boolean;
  subscriptionLoading: boolean;

  // Current tenant — readonly, derived from ActiveTenantProvider.
  // Switching tenant is `useActiveTenant().switchTo(id)`, not this context.
  currentTenantId: string | null;

  // Methods
  startTrial: (planSlug: string) => Promise<boolean>;
  convertTrial: (
    planSlug: string,
    billingCycle: 'monthly' | 'yearly',
    paymentMethodId: string
  ) => Promise<boolean>;
  changePlan: (
    newPlanSlug: string,
    newBillingCycle: 'monthly' | 'yearly'
  ) => Promise<{ success: boolean; requiresPayment?: boolean; redirectTo?: string; error?: string }>;
  upgradeTrialWithCheckout: (newPlanSlug: string, newBillingCycle: 'monthly' | 'yearly') => Promise<string | null>;
  cancelSubscription: (immediately?: boolean) => Promise<boolean>;
  refreshSubscription: () => Promise<void>;
  hasFeature: (featureName: string) => boolean;
}

const TenantSubscriptionContext = createContext<TenantSubscriptionContextType | undefined>(
  undefined
);

export function TenantSubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<{ id: string; email?: string } | null>(null);
  // Active tenant resolution is owned by ActiveTenantProvider (server-side
  // canonical). We just read from it — no parallel cookie/localStorage
  // reads here. That's the whole point of the refactor: one source of
  // truth for "which tenant am I viewing", everything else derives.
  const { tenantId: currentTenantId } = useActiveTenant();
  const supabase = createClient();

  // State
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<TenantSubscription | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [bypass, setBypass] = useState(false);

  // Loading states
  const [plansLoading, setPlansLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  // Tenant resolution is now `useActiveTenant()` above — no local
  // localStorage/user_tenants fallback. Legacy `fetchUserTenants` (which
  // queried the deprecated `user_tenants` table) is removed; the canonical
  // resolver reads `tenant_user_roles` instead.

  // Fetch subscription plans
  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const data = await getSubscriptionPlans();
      if (data.success && data.plans) {
        setPlans(data.plans);
      }
    } catch {
      // Cache drops the entry on failure — caller can refreshPlans()
    } finally {
      setPlansLoading(false);
    }
  };

  // Fetch current tenant subscription
  const fetchCurrentSubscription = async () => {
    if (!user || !currentTenantId) {
      setSubscriptionLoading(false);
      return;
    }

    try {
      setSubscriptionLoading(true);
      const response = await fetch(
        `/api/tenant-subscriptions/current?tenant_id=${currentTenantId}`
      );
      const data = await response.json();

      if (data.success) {
        setCurrentSubscription(data.subscription);
        setHasSubscription(data.hasSubscription);
        setIsTrialExpired(data.isTrialExpired || false);
        setTrialDaysRemaining(data.trialDaysRemaining || 0);
        setBypass(data.bypass || false);
      }
    } catch (error) {
      // Error handled('Error fetching subscription:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Start trial
  const startTrial = async (planSlug: string): Promise<boolean> => {
    if (!currentTenantId) return false;

    try {
      const response = await fetch('/api/tenant-subscriptions/trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: currentTenantId, planSlug }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchCurrentSubscription();
        return true;
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      // Error handled('Error starting trial:', error);
      return false;
    }
  };

  // Convert trial to paid
  const convertTrial = async (
    planSlug: string,
    billingCycle: 'monthly' | 'yearly',
    paymentMethodId: string
  ): Promise<boolean> => {
    if (!currentTenantId) return false;

    try {
      const response = await fetch('/api/tenant-subscriptions/convert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenantId,
          planSlug,
          billingCycle,
          paymentMethodId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchCurrentSubscription();
        return true;
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      // Error handled('Error converting trial:', error);
      return false;
    }
  };

  // Change plan
  const changePlan = async (
    newPlanSlug: string,
    newBillingCycle: 'monthly' | 'yearly'
  ): Promise<{ success: boolean; requiresPayment?: boolean; redirectTo?: string; error?: string }> => {
    if (!currentTenantId) {
      return { success: false, error: 'No tenant selected' };
    }

    try {
      const response = await fetch('/api/tenant-subscriptions/change-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenantId,
          newPlanSlug,
          newBillingCycle,
        }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchCurrentSubscription();
        return { success: true };
      } else {
        // Check if this is a trial user that needs payment
        if (data.requiresPayment) {
          return {
            success: false,
            requiresPayment: true,
            redirectTo: data.redirectTo,
            error: data.error,
          };
        }
        return { success: false, error: data.error };
      }
    } catch (error) {
      // Error handled('Error changing plan:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  // Upgrade trial with Stripe Checkout
  const upgradeTrialWithCheckout = async (
    newPlanSlug: string,
    newBillingCycle: 'monthly' | 'yearly'
  ): Promise<string | null> => {
    if (!currentTenantId) {
      console.error('No tenant ID found');
      return null;
    }

    try {
      const response = await fetch('/api/stripe/create-upgrade-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenantId,
          newPlanSlug,
          newBillingCycle,
          currency: readCurrencyCookie(), // Phase 2 — Display = Charge
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Checkout session API error:', {
          status: response.status,
          statusText: response.statusText,
          data,
        });
        throw new Error(data.error || data.details || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (data.success && data.url) {
        return data.url;
      } else {
        console.error('Checkout session missing URL:', data);
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  };

  // Cancel subscription
  const cancelSubscription = async (immediately = false): Promise<boolean> => {
    if (!currentTenantId) return false;

    try {
      const response = await fetch('/api/tenant-subscriptions/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId: currentTenantId,
          immediately,
        }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchCurrentSubscription();
        return true;
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      // Error handled('Error canceling subscription:', error);
      return false;
    }
  };

  // Refresh subscription data
  const refreshSubscription = async () => {
    await fetchCurrentSubscription();
  };

  // Check if user has access to a feature
  const hasFeature = (featureName: string): boolean => {
    if (!currentSubscription) return false;

    // If trial is expired, no access
    if (currentSubscription.status === 'trial' && isTrialExpired) {
      return false;
    }

    // If subscription is not active or trial, no access
    if (!['trial', 'active'].includes(currentSubscription.status)) {
      return false;
    }

    // Check plan features
    return currentSubscription.plan?.features[featureName] === true;
  };

  // Effects
  useEffect(() => {
    fetchPlans();

    // Get initial user
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const newUser = session?.user ?? null;

      // SECURITY: Clear tenant data when user changes or logs out.
      // The active-tenant cookie is also cleared in the logout handler
      // (AdminSidebar.handleLogout) and the canonical resolver picks the
      // new user's primary on next mount, so this is just local state.
      if (event === 'SIGNED_OUT' || (newUser?.id !== user?.id && user !== null)) {
        localStorage.removeItem('currentTenantId');
        localStorage.removeItem('adminViewAsTenant');
        sessionStorage.removeItem('currentTenantId');
        setCurrentSubscription(null);
        setHasSubscription(false);
      }

      setUser(newUser);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    fetchCurrentSubscription();
  }, [user, currentTenantId]);

  // Supabase-Realtime subscription on `tenant_subscriptions` — fires
  // whenever the Stripe webhook UPDATEs this tenant's row (status flip
  // trial → active, cancel_at_period_end toggled, etc). Without this
  // the UI stayed stuck on the pre-webhook snapshot until hard refresh.
  // Real-Card-Test 2026-05-29 surfaced this as "Kein Plan aktiv"
  // showing even after successful checkout. Cheap: one channel per
  // active tenant, only invalidates on actual mutation.
  useEffect(() => {
    if (!currentTenantId) return;
    const supabase = createClient();
    const channel = supabase
      .channel(`tenant-subs:${currentTenantId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tenant_subscriptions',
          filter: `tenant_id=eq.${currentTenantId}`,
        },
        () => {
          void fetchCurrentSubscription();
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
    // fetchCurrentSubscription is stable for this effect's purpose — it
    // reads from refs, not from the dependency array. Avoiding it here
    // prevents a re-subscribe storm on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  const value: TenantSubscriptionContextType = {
    // Data
    plans,
    currentSubscription,
    hasSubscription,
    isTrialExpired,
    trialDaysRemaining,
    bypass,

    // Loading states
    plansLoading,
    subscriptionLoading,

    // Current tenant (readonly)
    currentTenantId,

    // Methods
    startTrial,
    convertTrial,
    changePlan,
    upgradeTrialWithCheckout,
    cancelSubscription,
    refreshSubscription,
    hasFeature,
  };

  return (
    <TenantSubscriptionContext.Provider value={value}>
      {children}
    </TenantSubscriptionContext.Provider>
  );
}

export function useTenantSubscription() {
  const context = useContext(TenantSubscriptionContext);
  if (context === undefined) {
    throw new Error('useTenantSubscription must be used within a TenantSubscriptionProvider');
  }
  return context;
}
